<script setup lang="ts">
import { ref, onMounted } from 'vue';
import axios from 'axios';
import { format } from 'date-fns';

const iconComponent = (iconName: string) => {
  const url = `../../assets/weather-icons/${iconName}.svg` 
  console.log('URL:', url);
  return new URL( url, import.meta.url).href;
}

let weather_icons = ref({
    "01d": "clear-sky",
    "01n": "clear-sky",
    "02d": "few-clouds",
    "02n": "few-clouds",
    "03d": "scattered-clouds",
    "03n": "scattered-clouds",
    "04d": "broken-clouds",
    "04n": "broken-clouds",
    "09d": "shower-rain",
    "09n": "shower-rain",
    "10d": "rain",
    "10n": "rain",
    "11d": "thunderstorm",
    "11n": "thunderstorm",
    "13d": "snow",
    "13n": "snow",
    "50d": "mist",
    "50n": "mist"
})


interface WeatherData {
  current: {
    temperature_2m: number;
    weather_code: number;
  };
}

const temperature = ref<number | null>(null);
const weatherDescription = ref('');
const weatherIcon = ref('');
const loading = ref(true);
const error = ref('');
const currentDate = ref(new Date());
const location = ref('');
const state = ref('');

const getWeatherInfo = (code: number) => {
  if (code === 0) return { description: 'Despejado', icon: '01d' };
  if (code === 1) return { description: 'Mayormente despejado', icon: '02d' };
  if (code === 2) return { description: 'Parcialmente nublado', icon: '03d' };
  if (code === 3) return { description: 'Nublado', icon: '04d' };
  if (code >= 51 && code <= 55) return { description: 'Llovizna', icon: '09d' };
  if (code >= 61 && code <= 65) return { description: 'Lluvia', icon: '10d' };
  if (code >= 71 && code <= 75) return { description: 'Nieve', icon: '13d' };
  if (code >= 95 && code <= 99) return { description: 'Tormenta', icon: '11d' };
  return { description: 'Desconocido', icon: '50d' };
};

const getLocation = async (latitude: number, longitude: number) => {
  try {
    // Usando la API de geocodificación inversa de Nominatim (OpenStreetMap)
    const response = await fetch(
      `https://nominatim.openstreetmap.org/reverse?format=json&lat=${latitude}&lon=${longitude}&accept-language=es`
    );
    const data = await response.json();
    
    // Extraer el departamento/ciudad y el país
    const city = data.address.city || data.address.town || data.address.village || data.address.municipality;
    const _state = data.address.state;
    const country = data.address.country;
    
    location.value = `${city}, ${_state}`;
    state.value = `${_state}`;
    console.log('Ubicación:', data.address);
  } catch (e) {
    console.error('Error al obtener la ubicación:', e);
    location.value = 'Ubicación no disponible';
  }
};

const getWeather = async () => {
  try {
    const position = await new Promise<GeolocationPosition>((resolve, reject) => {
      navigator.geolocation.getCurrentPosition(resolve, reject);
    });
    
    const { latitude, longitude } = position.coords;

    // Obtener la ubicación
    await getLocation(latitude, longitude);

    const weatherResponse = await axios.get<WeatherData>(
      `https://api.open-meteo.com/v1/forecast?latitude=${latitude}&longitude=${longitude}&current=temperature_2m,weather_code&  =true`
    );

    temperature.value = Math.round(weatherResponse.data.current.temperature_2m);
    const weatherInfo = getWeatherInfo(weatherResponse.data.current.weather_code);
    weatherDescription.value = weatherInfo.description;
    weatherIcon.value = weatherInfo.icon;
    loading.value = false;
  } catch (e) {
    console.error('Error del clima:', e);
    error.value = 'Error al cargar los datos del clima. Por favor, permite el acceso a tu ubicación.';
    loading.value = false;
  }
};

onMounted(() => {
  getWeather();
});
</script>
<template>
  <div class="--weather-card">
    <div v-if="loading" class="loading">
      <div class="loading-content">
        <div class="loading-circle"></div>
          Cargando datos del clima...
      </div>
    </div>
    <div v-else-if="error" class="error">
      {{ error }}
      <button @click="getWeather" class="retry-button">
        Reintentar
      </button>
    </div>
    
    <div v-else class="weather-content">
      <div class="inner">
        <div class="city-space">
          <div class="location">
           {{ state }}
          </div>
           <p class="time">{{ format(currentDate, 'EEEE') }} {{ format(currentDate, 'HH:mm a') }}</p>
        </div>
         <div class="weather-info">
        
            <div class="temperature-value"> 
              <span class="temperature">{{ temperature }}°</span>
              <div class="icon">
                
              </div>
            </div>
            <!-- 
            <div class="weather-description">
              <span >{{ weatherDescription }}</span>
            </div>
            -->
          </div>
      </div>      
     
    </div>
  </div>
</template>

<style scoped>
.--weather-card {
  border-radius: 16px;
  overflow: hidden;
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
  background-image: url('https://images.unsplash.com/photo-1506929562872-bb421503ef21?q=80&w=1368&auto=format&fit=crop&ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D');
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  .weather-content {
    display: flex;
    flex-direction: column;
    box-sizing: border-box;
    max-width: 100%;
    gap: 1.5rem;
    .inner {
      padding: 12px;
      display: flex;
      gap: 12px;
      justify-content: space-between;
      color: #222;
      background: rgba(255, 255, 255, 0.7);
      backdrop-filter: blur(10px);
      align-items: center;
      .city-space {
        font-size: 10px;
        .location {
          font-weight: 800;
            font-size: 14px;
        }
      }
      .temperature-value {
        display: flex;
        gap: 4px;
        align-items: center;
        .temperature {
          font-size: 2rem;
          font-weight: 500;
        }
        .icon {
          display: flex;
          justify-content: center;
          align-items: center;
          .weather-icon {
            width: 50px;
            height: 50px;
          }
        }
      }
    }
}
  
}

.loading {
  text-align: center;
  padding: 2rem 0;
}

.loading-content {
  display: flex;
  flex-direction: column;
  align-items: center;
}

.loading-circle {
  width: 4rem;
  height: 4rem;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 50%;
  margin-bottom: 1rem;
  animation: pulse 2s infinite;
}

.error {
  text-align: center;
  padding: 2rem 0;
     backdrop-filter: blur(10px);
}

.retry-button {
  margin-top: 1rem;
  padding: 0.5rem 1.5rem;
  border-radius: 0.5rem;
  background: rgba(255, 255, 255, 0.1);
  transition: all 0.3s;
}

.retry-button:hover {
  background: rgba(255, 255, 255, 0.3);
  transform: scale(1.05);
}


.location {
  font-weight: 500;
}

.date-temp {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.day {
  font-size: 1.5rem;
  font-weight: 500;
}

.time {
  opacity: 0.9;
  margin: 0;
}

.temperature {
  font-size: 30px;
  font-weight: 400;
}

.weather-info {
  display: flex;
  flex-direction: column;
  gap: 4px;
  background: rgba(255, 255, 255, 0.4);
  border-radius: 1rem;
  transition: all 0.3s;
  font-size: 12px;
  padding: 1rem;
  .day {
    font-size: 12px;
  }
  img {
    width: 30px;
    height: 30px;
  }
}

.weather-info:hover {
  background: rgba(255, 255, 255, 0.2);
}


.weather-description {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

@keyframes pulse {
  0% { opacity: 0.5; }
  50% { opacity: 1; }
  100% { opacity: 0.5; }
}
</style>